import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Route, Redirect, Switch } from 'react-router-dom';
import { ConnectedRouter as Router } from 'react-router-redux';
import { ToastContainer } from 'react-toastr';
import utils, { constants } from '@utils';
import { servicesInitializer } from '@services';
import cacheSystem from '@utils/cacheSystem';

import { QuickPay } from '@pages/quickpay';
import { InvoiceEstimate } from '@pages/invoiceEstimate';
import LocalServicesInitializer from '@services/_initializer';
import GlobalsActions from '@redux/globals';
import config from '@config';
import {
	SplashScreen, PrivateRoute, Error404, ADCallback,
} from '@components';
import Login from '@pages/login';
import ResetPassword from '@pages/resetPassword';
import Logout from '@pages/logout';
import JobsList from '@pages/jobs/list';
import JobsMap from '@pages/jobs/map';
import JobView from '@pages/jobs/view';
import LocationDetails from '@pages/jobs/components/locationDetails';
import PaymentsList from '@pages/payments';
import PaymentsView from '@pages/payments/view';
import PaymentSummary from '@pages/payments/paymentSummary';
import PaymentReceipt from '@pages/payments/paymentReceipt';
import PaymentSignUp from '@pages/payments/signUp';
import LocationsList from '@pages/locations';
import LocationView from '@pages/locations/view';
import CoordinatorsList from '@pages/coordinators';
import CoordinatorsDetails from '@pages/coordinators/view';
import Account from '@pages/account';
import HistoryReports from '@pages/reports';
import Staff from '@pages/staff';
import { withMsal } from '@azure/msal-react';
import { thunks } from '@redux/app';
import { ROUTES } from '@constants';
import MarketPlace from '../../pages/marketplace';
import PrepaySignup from '../../pages/prepay/signup';
import PrepayPayment from '../../pages/prepay/payment';
import Overview from '../../pages/overview';
import ClientSignUp from '../../pages/ClientSignUp/ClientSignUp';
import SignupRedirect from '../../pages/ClientSignUp/ADSignUp/SignUpRedirect';
import ADConfirmation from '../../pages/ClientSignUp/ADConfirmation';
import RequestJob from '../../pages/requestjob';
import { ADOPERATIONS } from '../utils/constants';

class Navigation extends Component {
	UNSAFE_componentWillMount() {
		const { unauthorizedEvent } = this.props;

		// Register notify event
		utils.registerEventListener('notify', this.notify);
		utils.registerEventListener('unauthorized', unauthorizedEvent);

		servicesInitializer({
			apiUrl      : config.api.url,
			googleKey   : constants.GOOGLE_MAP_SETTINGS.key,
			geocoderURL : constants.GOOGLE_MAP_SETTINGS.geocoderURL,
			options    	: { authTokenCallback : this.getAuthToken },
		});

		LocalServicesInitializer({
			apiUrl  : config.api.url,
			options : { authTokenCallback : this.getAuthToken },
		});

		cacheSystem.setup('rk-client');
	}

	componentDidMount() {
		const { init } = this.props;

		init();
	}

	shouldComponentUpdate = ({ isAuthenticated, isInitiated, loading }) => {
		const lastProps = this.props;

		return (
			isAuthenticated !== lastProps.isAuthenticated
			|| isInitiated !== lastProps.isInitiated
			|| loading !== lastProps.loading
		);
	}

	notify = (parameters) => {
		const optionsOverride = {
			closeButton     : parameters.closeButton || false,
			timeOut         : parameters.timeOut || 6000,
			showAnimation   : parameters.showAnimation || 'animated flipInX',
			hideAnimation   : parameters.hideAnimation || 'animated flipOutX',
			extendedTimeOut : null,
		};

		this._notificationSystem[parameters.type](parameters.message || '', parameters.title || '', optionsOverride);
	}

	getAuthToken = () => {
		const {
			msalContext: { instance: msalInstance },
		} = this.props;

		const { adb2cEnabled } = window;

		if (adb2cEnabled) {
			return msalInstance.acquireTokenSilent({
				account : msalInstance.getAccountByHomeId(localStorage.getItem('homeAccountId')),
				scopes  : config.ENV_OPTIONS.adb2c.scopes,
			})
				.then((accessTokenResponse) => accessTokenResponse.accessToken)
				.catch(() => {
					utils.dispatchUnauthorizedMessage();
				});
		}
		return Promise.resolve(localStorage.getItem('t'));
	}

	render() {
		const {
			history,
			isAuthenticated,
			isInitiated,
			loading,
			setRouteDetails,
		} = this.props;

		if (!isInitiated || loading) return <SplashScreen />;

		const privateRouteProps = {
			isAuthenticated,
			setRouteDetails,
		};

		const { adb2cEnabled } = window;

		return (
			<Router history={history}>
				<div>
					<ToastContainer
						ref={(ref) => { this._notificationSystem = ref; }}
						preventDuplicates={false}
						className="toast-top-right"
					/>

					<Switch>
						{/* Adding backward compatibility with the old signup urls */}
						<If condition={adb2cEnabled}>
							<Route
								exact
								path="/signup"
								render={({ location }) => (
									<Redirect to={{ ...location, pathname : ROUTES.signupClientAD }} />
								)}
							/>
							<Route
								exact
								strict
								path={ROUTES.ADCallback}
								component={ADCallback}
							/>
						</If>
						<Route
							path="/quickpay/:type/:hash"
							component={QuickPay}
						/>
						<Route
							path="/invoiceestimate/:type/:hash"
							component={InvoiceEstimate}
						/>
						<Route
							exact
							strict
							path="/login"
							component={Login}
						/>
						<Route
							exact
							strict
							path="/logout"
							component={Logout}
						/>
						<Route
							path="/activation"
							component={({ location }) => (
								<Redirect
									to={{
										...location,
										pathname : location.pathname.replace(/activation/, 'signup'),
									}}
								/>
							)}
						/>
						<If condition={adb2cEnabled}>
							<Route
								path={ROUTES.signupConfirmation}
								component={ADConfirmation}
							/>
							<Route
								path={ROUTES.resetPassword}
								component={(props) => (
									<SignupRedirect
										// eslint-disable-next-line react/jsx-props-no-spreading
										{...props}
										redirectPolicy={config.ENV_OPTIONS.adb2c.policies.resetPassword}
										operation={ADOPERATIONS.resetPassword}
									/>
								)}
							/>
							<Route
								path={ROUTES.signupClientAD}
								component={(props) => (
									<SignupRedirect
										// eslint-disable-next-line react/jsx-props-no-spreading
										{...props}
										redirectPolicy={config.ENV_OPTIONS.adb2c.policies.signupClient}
										operation={ADOPERATIONS.signup}
									/>
								)}
							/>
						</If>

						<If condition={!adb2cEnabled}>
							<Route
								exact
								strict
								path="/signup"
								component={ClientSignUp}
							/>
						</If>
						<Route
							exact
							strict
							path="/resetPassword"
							component={ResetPassword}
						/>
						<Redirect
							exact
							strict
							from="/"
							to="/overview"
						/>
						<Redirect
							exact
							strict
							from="/jobs"
							to="/jobs/list"
						/>
						<Redirect
							exact
							strict
							from="/jobs/list"
							to="/jobs/list/active"
						/>
						<Redirect
							exact
							strict
							from="/jobs/map"
							to="/jobs/map/active"
						/>
						<PrivateRoute
							path="/jobs/list/:viewType"
							component={JobsList}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							path="/jobs/map/:viewType"
							component={JobsMap}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							exact
							path="/jobs/:id"
							component={JobView}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							exact
							path="/jobs/:id/locationDetails"
							component={LocationDetails}
							privateProps={privateRouteProps}
						/>
						<Redirect
							exact
							strict
							from="/"
							to="/payments"
						/>
						<Redirect
							exact
							strict
							from="/payments"
							to="/payments/list"
						/>
						<Redirect
							exact
							strict
							from="/payments/list"
							to="/payments/list/payment"
						/>
						<PrivateRoute
							path="/payments/list/:viewType"
							component={PaymentsList}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							path="/payments/invoice"
							component={PaymentSummary}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							path="/payments/receipt/:id"
							component={PaymentReceipt}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							path="/payments/signup"
							component={PaymentSignUp}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							path="/payments/:id"
							component={PaymentsView}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							exact
							path="/locations"
							component={LocationsList}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							exact
							path="/locations/:id"
							component={LocationView}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							exact
							path="/overview"
							component={Overview}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							exact
							path="/coordinators"
							component={CoordinatorsList}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							exact
							path="/coordinators/:id"
							component={CoordinatorsDetails}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							exact
							path="/account"
							component={Account}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							exact
							path="/reports"
							component={HistoryReports}
							privateProps={privateRouteProps}
						/>
						<PrivateRoute
							exact
							path="/staff"
							component={Staff}
							privateProps={privateRouteProps}
						/>
						<Route
							exact
							path="/hireoffduty"
							component={MarketPlace}
						/>
						<Route
							exact
							path="/prepay/signup/:estId?"
							component={PrepaySignup}
						/>
						<Route
							exact
							path="/requestjob"
							component={RequestJob}
						/>
						<PrivateRoute
							exact
							path="/prepay/payment/:estId?"
							component={PrepayPayment}
							privateProps={privateRouteProps}
							showNavBar={false}
						/>
						<Route component={Error404} />
					</Switch>
				</div>
			</Router>
		);
	}
}

const mapStateToProps = ({
	app : {
		isAuthenticated,
		isInitiated,
		loading,
	},
}) => ({
	isAuthenticated,
	isInitiated,
	loading,
});

const mapDispatchToProps = (dispatch) => ({
	dispatch,
	setRouteDetails   : bindActionCreators(GlobalsActions, dispatch).setRouteDetails,
	unauthorizedEvent : bindActionCreators(thunks, dispatch).unauthorizedEvent,
	init              : bindActionCreators(thunks, dispatch).init,
});

Navigation.propTypes = {
	history           : PropTypes.object.isRequired,
	isAuthenticated   : PropTypes.bool,
	isInitiated       : PropTypes.bool,
	loading           : PropTypes.bool,
	setRouteDetails   : PropTypes.func.isRequired,
	msalContext       : PropTypes.object.isRequired,
	unauthorizedEvent : PropTypes.func.isRequired,
	init              : PropTypes.func.isRequired,
};

Navigation.defaultProps = {
	isAuthenticated : false,
	isInitiated     : false,
	loading         : false,
};

export default withMsal(connect(mapStateToProps, mapDispatchToProps)(Navigation));
